import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Row, Col, Card } from "react-bootstrap";
import { 
    FaUser, 
    FaIdCard, 
    FaShieldAlt, 
    FaStickyNote, 
    FaMapMarkerAlt, 
    FaSignInAlt, 
    FaFileContract, 
    FaGlobe, 
    FaCalendarAlt,
    FaIndustry,
    FaKey,
    FaInfoCircle
} from "react-icons/fa";

const TenantView = ({ tenantData, setTenantDetails }) => {
    const [editedTenantData, setEditedTenantData] = useState(tenantData);
    const [industries, setIndustries] = useState([]);
    const didFetchRef = useRef(false);

    const fetchIndustries = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/industries`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader:true,
            });

            setIndustries(response.data.industry || []);
        } catch (error) {
            console.error('Error fetching industries:', error);
        }
    };

    useEffect(() => {
        if (!didFetchRef.current) {
            fetchIndustries();
            didFetchRef.current = true;
        }
    }, []);

    useEffect(() => {
        setEditedTenantData(tenantData);
    }, [tenantData]);

    // Helper function to get display value
    const getDisplayValue = (value) => {
        return value || "--";
    };

    // Helper function to format option labels
    const getOptionLabel = (value, options) => {
        if (!value) return "--";
        const option = options?.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    // Helper function to get industry name
    const getIndustryName = (id) => {
        if (!id) return "--";
        const industry = industries.find((ind) => Number(ind.id) === Number(id));
        return industry ? industry.name : "--";
    };

    // Field configuration with icons and categories
    const fieldGroups = [
        {
            title: "Basic Information",
            icon: <FaUser className="text-primary" />,
            fields: [
                { 
                    name: 'name', 
                    label: 'Name', 
                    icon: <FaUser size={14} />, 
                    value: getDisplayValue(editedTenantData?.name) 
                },
                { 
                    name: 'tenant_info_id', 
                    label: 'Tenant ID', 
                    icon: <FaIdCard size={14} />, 
                    value: getDisplayValue(editedTenantData?.tenant_info_id) 
                },
                { 
                    name: 'account_status', 
                    label: 'Account Status', 
                    icon: <FaShieldAlt size={14} />, 
                    value: getOptionLabel(editedTenantData?.account_status, [
                        { value: "active", label: "Active" }, 
                        { value: "deactivated", label: "Deactivated" }, 
                        { value: "suspended", label: "Suspended" }
                    ]) 
                },
                { 
                    name: 'status_note', 
                    label: 'Status Note', 
                    icon: <FaStickyNote size={14} />, 
                    value: getDisplayValue(editedTenantData?.status_note) 
                }
            ]
        },
        {
            title: "Contact & Location",
            icon: <FaMapMarkerAlt className="text-info" />,
            fields: [
                { 
                    name: 'address', 
                    label: 'Address', 
                    icon: <FaMapMarkerAlt size={14} />, 
                    value: getDisplayValue(editedTenantData?.address) 
                },
                { 
                    name: 'login', 
                    label: 'Login', 
                    icon: <FaSignInAlt size={14} />, 
                    value: getDisplayValue(editedTenantData?.login) 
                },
                { 
                    name: 'ips', 
                    label: 'IP Address', 
                    icon: <FaGlobe size={14} />, 
                    value: getDisplayValue(editedTenantData?.ips) 
                }
            ]
        },
        {
            title: "Contract & License",
            icon: <FaFileContract className="text-warning" />,
            fields: [
                { 
                    name: 'contract_type', 
                    label: 'Contract Type', 
                    icon: <FaFileContract size={14} />, 
                    value: getOptionLabel(editedTenantData?.contract_type, [
                        { value: "basic", label: "Basic" }, 
                        { value: "standard", label: "Standard" }, 
                        { value: "premium", label: "Premium" }
                    ]) 
                },
                { 
                    name: 'license_type', 
                    label: 'License Type', 
                    icon: <FaKey size={14} />, 
                    value: getOptionLabel(editedTenantData?.license_type, [
                        { value: "subscription", label: "Subscription" }, 
                        { value: "perpetual", label: "Perpetual" }
                    ]) 
                },
                { 
                    name: 'subscription_period', 
                    label: 'Subscription Period', 
                    icon: <FaCalendarAlt size={14} />, 
                    value: getOptionLabel(editedTenantData?.subscription_period, [
                        { value: "monthly", label: "Monthly" }, 
                        { value: "quarterly", label: "Quarterly" }, 
                        { value: "halfyearly", label: "Half Yearly" }, 
                        { value: "yearly", label: "Yearly" }, 
                        { value: "subscription free", label: "Subscription Free" }
                    ]),
                    condition: editedTenantData?.license_type === 'subscription'
                },
                { 
                    name: 'anc_start_date', 
                    label: 'AMC Start Date', 
                    icon: <FaCalendarAlt size={14} />, 
                    value: getDisplayValue(editedTenantData?.anc_start_date),
                    condition: editedTenantData?.license_type === 'perpetual'
                }
            ]
        },
        {
            title: "Additional Information",
            icon: <FaIndustry className="text-success" />,
            fields: [
                { 
                    name: 'industry_id', 
                    label: 'Industry', 
                    icon: <FaIndustry size={14} />, 
                    value: getIndustryName(editedTenantData?.industry_id) 
                },
                { 
                    name: 'setup_start_date', 
                    label: 'Setup Start Date', 
                    icon: <FaCalendarAlt size={14} />, 
                    value: getDisplayValue(editedTenantData?.setup_start_date) 
                },
                { 
                    name: 'domain', 
                    label: 'Domain', 
                    icon: <FaGlobe size={14} />, 
                    value: getDisplayValue(editedTenantData?.domain?.domain_name) 
                }
            ]
        }
    ];

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="badge bg-success">Active</span>;
            case 'deactivated':
                return <span className="badge bg-warning">Deactivated</span>;
            case 'suspended':
                return <span className="badge bg-danger">Suspended</span>;
            default:
                return <span className="badge bg-secondary">Unknown</span>;
        }
    };

    return (
        <div className="p-3">
            {/* Tenant Header Card */}
            {/* <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="text-center">
                    <div className="d-flex align-items-center justify-content-center mb-3">
                        <div className="bg-primary rounded-circle p-3">
                            <FaUser className="text-white" size={32} />
                        </div>
                    </div>
                    <h4 className="fw-bold text-dark mb-1">{getDisplayValue(editedTenantData?.name)}</h4>
                    <p className="text-muted mb-2">Tenant ID: {getDisplayValue(editedTenantData?.tenant_info_id)}</p>
                    <div className="d-flex justify-content-center gap-4">
                        <div className="d-flex align-items-center text-muted">
                            <FaShieldAlt className="me-2" />
                            <small>{getStatusBadge(editedTenantData?.account_status)}</small>
                        </div>
                        <div className="d-flex align-items-center text-muted">
                            <FaFileContract className="me-2" />
                            <small className="text-capitalize">
                                {getOptionLabel(editedTenantData?.contract_type, [
                                    { value: "basic", label: "Basic" }, 
                                    { value: "standard", label: "Standard" }, 
                                    { value: "premium", label: "Premium" }
                                ])}
                            </small>
                        </div>
                        <div className="d-flex align-items-center text-muted">
                            <FaKey className="me-2" />
                            <small className="text-capitalize">
                                {getOptionLabel(editedTenantData?.license_type, [
                                    { value: "subscription", label: "Subscription" }, 
                                    { value: "perpetual", label: "Perpetual" }
                                ])}
                            </small>
                        </div>
                    </div>
                </Card.Body>
            </Card> */}

            {/* Tenant Details Sections */}
            {fieldGroups.map((group, groupIndex) => {
                // Filter fields based on conditions
                const visibleFields = group.fields.filter(field => 
                    field.condition === undefined || field.condition === true
                );

                if (visibleFields.length === 0) return null;

                return (
                    <Card key={groupIndex} className="mb-4 shadow-sm border-0">
                        <Card.Header className="bg-light border-0 py-3">
                            <div className="d-flex align-items-center">
                                {group.icon}
                                <h5 className="mb-0 ms-2 fw-semibold text-dark">{group.title}</h5>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Row className="g-0">
                                {visibleFields.map((field, fieldIndex) => (
                                    <Col md={6} key={fieldIndex}>
                                        <div className="p-3 border-bottom border-end tenant-detail-item">
                                            <div className="d-flex align-items-start">
                                                <div className="text-muted me-3 mt-1">
                                                    {field.icon}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <label className="text-muted small mb-1 d-block fw-medium">
                                                        {field.label}
                                                    </label>
                                                    <div className="text-dark fw-semibold">
                                                        {field.value}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                );
            })}

            {/* System Information Card */}
            {/* <Card className="shadow-sm border-0">
                <Card.Header className="bg-light border-0 py-3">
                    <div className="d-flex align-items-center">
                        <FaInfoCircle className="text-secondary" />
                        <h5 className="mb-0 ms-2 fw-semibold text-dark">System Information</h5>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="text-muted small mb-1 d-block fw-medium">Tenant ID</label>
                                <div className="text-dark fw-semibold">
                                    {tenantData?.id || "--"}
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="text-muted small mb-1 d-block fw-medium">Created Date</label>
                                <div className="text-dark fw-semibold">
                                    {tenantData?.created_at ? new Date(tenantData.created_at).toLocaleDateString() : "--"}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card> */}
        </div>
    );
};

export default TenantView;
